import { useEffect, useState } from 'react';

import antdLight from './antd-light.lazy.css';
import antdDark from './antd-dark.lazy.css';
import './theme.scss';
import './antd-overrides.scss';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'cvat-ui-theme';
const stylesheets = { light: antdLight, dark: antdDark };
const listeners = new Set<(theme: Theme) => void>();
let current: Theme | null = null;

function readStoredTheme(): Theme {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'light' || stored === 'dark') return stored;
    } catch {
        // storage blocked: fall through to the default
    }
    return 'dark';
}

function applyTheme(theme: Theme): void {
    if (theme === current) return;
    // mount the new antd build before dropping the old one, so nothing renders unstyled
    stylesheets[theme].use();
    if (current) stylesheets[current].unuse();
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
    current = theme;
    listeners.forEach((listener) => listener(theme));
}

export function getTheme(): Theme {
    return current ?? readStoredTheme();
}

export function setTheme(theme: Theme): void {
    try {
        localStorage.setItem(STORAGE_KEY, theme);
    } catch {
        // the choice just won't survive a reload
    }
    applyTheme(theme);
}

export function useTheme(): [Theme, (theme: Theme) => void] {
    const [theme, setState] = useState<Theme>(getTheme);
    useEffect(() => {
        listeners.add(setState);
        return () => { listeners.delete(setState); };
    }, []);
    return [theme, setTheme];
}

applyTheme(readStoredTheme());
window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) applyTheme(readStoredTheme());
});
